import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ChannelContext } from '../../../../context';
import { Event } from '../../../../services';
import {
  Button,
  ButtonType,
  Modal,
  Select,
  SelectItem,
  useAppDispatch,
  useAppSelector,
} from '../../../../shared';
import { eventUpdateChannels } from '../../../../slices';

import classes from './EditChannelsModal.module.scss';

interface EditChannelsModalProps {
  isOpened: boolean;
  onClose: () => void;
  event: Event;
}

export const EditChannelsModal: FunctionComponent<EditChannelsModalProps> = ({
  isOpened,
  onClose,
  event,
}) => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'editEventChannels' });

  const dispatch = useAppDispatch();

  const { channels } = useAppSelector(({ channels }) => channels);

  const { id: channelId } = useContext(ChannelContext).channel;

  const channelSelectItems = useMemo<SelectItem<number>[]>(
    () => channels.map(({ id, name }) => ({ id, title: name })),
    [channels]
  );

  const { id: eventId, channels: eventChannels } = event;

  const [channelIds, setChannelIds] = useState<number[]>(eventChannels.map(({ id }) => id));

  const onSave = useCallback(async () => {
    try {
      await toast.promise(
        dispatch(eventUpdateChannels({ channelId, eventId, fields: { channelIds } })).unwrap(),
        { pending: ctxT('pending'), success: ctxT('success'), error: ctxT('error') }
      );

      onClose();
    } catch {}
  }, [channelId, channelIds, ctxT, dispatch, eventId, onClose]);

  const body = useMemo(
    () => (
      <div className={classes['edit-channels']}>
        <Select
          label={t('common.channels_other')}
          items={channelSelectItems}
          selectedItemIds={channelIds}
          onMultiCheckChange={setChannelIds}
          staticListPosition
          search
        />
        <Button
          type={ButtonType.primary}
          label={t('common.save')}
          onClick={onSave}
          disabled={!Boolean(channelIds.length)}
        />
      </div>
    ),
    [channelIds, channelSelectItems, onSave, t]
  );

  return (
    <Modal
      isOpen={isOpened}
      body={body}
      onClose={onClose}
      ariaLabel={t('eventActions.edit-channels')}
      alignTop
      disableCloseByOverlay
    />
  );
};
